import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, FileText, Sparkles, RefreshCw, ExternalLink, Tag,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '../services/client';
import {
  type PageInsight,
  mapAnalysisPayload,
  isLowValueTabUrl,
} from './contentAnalysisUtils';

const AdvancedContentAnalysis: React.FC = () => {
  const [insights, setInsights] = useState<PageInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/sessions');
      const sessions = Array.isArray(res.data) ? res.data : (res.data?.sessions ?? []);
      const activeSession =
        sessions.find((s: { is_active?: boolean; active?: boolean }) => s.is_active || s.active) ??
        sessions[0];
      if (!activeSession) {
        setInsights([]);
        return;
      }

      const tabsRes = await api.get(`/sessions/${activeSession.id}/tabs`);
      const tabs = (tabsRes.data?.tabs ?? tabsRes.data ?? [])
        .filter((t: { url?: string }) => !isLowValueTabUrl(t.url))
        .slice(0, 6);

      const results = await Promise.all(
        tabs.map(async (t: { id: string; title?: string; url?: string }) => {
          try {
            const a = await api.post(`/tabs/${t.id}/analyze`, {}, { timeout: 120000 });
            const mapped = mapAnalysisPayload(
              t.id,
              t.title ?? '',
              t.url,
              (a.data ?? {}) as Record<string, unknown>,
            );
            if (!mapped.summary && mapped.keyInsights.length === 0) return null;
            return mapped;
          } catch {
            return null;
          }
        }),
      );

      setInsights(results.filter(Boolean) as PageInsight[]);
    } catch (err) {
      console.error(err);
      setError('Could not load tab insights. Sync tabs from the browser extension and try again.');
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const avgConfidence =
    insights.length > 0
      ? `${Math.round(insights.reduce((sum, i) => sum + i.confidence, 0) / insights.length)}%`
      : '—';

  const categoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'research':
      case 'technical':
        return 'bg-info-500/20 text-info-400';
      case 'news':
        return 'bg-warning-500/20 text-warning-400';
      case 'shopping':
        return 'bg-success-500/20 text-success-400';
      default:
        return 'bg-primary-500/20 text-primary-400';
    }
  };

  return (
    <PageLayout
      title="Advanced Content Analysis"
      description="AI-powered content insights and summarization"
      icon={TrendingUp}
    >
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-khiimori-text-muted">
          Summaries and key points from your synced workspace tabs
        </p>
        <button
          type="button"
          onClick={loadInsights}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-khiimori-border/20 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Pages Analyzed', value: insights.length, textClass: 'text-warning-500' },
          { label: 'Key Insights', value: insights.reduce((n, i) => n + i.keyInsights.length, 0), textClass: 'text-primary-400' },
          { label: 'Avg Confidence', value: avgConfidence, textClass: 'text-success-500' },
          { label: 'Topics Found', value: insights.reduce((n, i) => n + i.topics.length, 0), textClass: 'text-info-400' },
        ].map((stat, index) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}>
            <Card hover>
              <div className="text-center">
                <div className={`text-2xl font-bold ${stat.textClass}`}>{stat.value}</div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-error-500/20 bg-error-500/10 px-4 py-3 text-sm text-error-400">
          {error}
        </div>
      )}

      <div className="space-y-5">
        {loading && (
          <p className="text-sm text-khiimori-text-muted py-4">Analyzing workspace tabs…</p>
        )}
        {!loading && insights.length === 0 && (
          <p className="text-sm text-khiimori-text-muted py-8 text-center">
            No insights yet. Open pages in your browser and sync tabs from the Live tab.
          </p>
        )}

        {insights.map((insight, index) => (
          <motion.div key={insight.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
            <Card hover className="overflow-hidden">
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${categoryColor(insight.category)}`}>
                        {insight.category}
                      </span>
                      <span className="text-xs text-khiimori-text-muted">{insight.sentiment} tone</span>
                      <span className="text-xs font-semibold text-warning-500">{insight.confidence}% confidence</span>
                    </div>
                    <h3 className="text-lg font-bold text-khiimori-text-primary truncate">{insight.title}</h3>
                    <p className="text-xs text-khiimori-text-muted mt-1 flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />
                      {insight.source}
                    </p>
                  </div>
                </div>

                {insight.summary && (
                  <div className="rounded-xl bg-khiimori-bg-elevated/10 border border-khiimori-border/10 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-khiimori-text-primary mb-2">
                      <FileText className="h-4 w-4 text-primary-400" />
                      Summary
                    </div>
                    <p className="text-sm text-khiimori-text-secondary leading-relaxed">{insight.summary}</p>
                  </div>
                )}

                {insight.keyInsights.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-khiimori-text-primary mb-2">
                      <Sparkles className="h-4 w-4 text-warning-400" />
                      Key insights
                    </div>
                    <ul className="space-y-2">
                      {insight.keyInsights.map((line, i) => (
                        <li key={i} className="flex gap-2 text-sm text-khiimori-text-secondary">
                          <span className="text-primary-400 mt-0.5">•</span>
                          <span>{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {insight.topics.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Tag className="h-4 w-4 text-khiimori-text-muted" />
                    {insight.topics.map((topic) => (
                      <span
                        key={topic}
                        className="px-2 py-1 rounded-full text-xs bg-khiimori-bg-elevated/20 text-khiimori-text-muted border border-khiimori-border/10"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </PageLayout>
  );
};

export default AdvancedContentAnalysis;
